/**
 * 사이드바 컴포넌트
 * - 역할별 메뉴 필터링
 * - 현재 경로 하이라이트
 * - 접기/펼치기 기능
 * - 모바일 환경에서 Drawer 모드
 */

import React, { useMemo } from 'react';
import { Layout, Menu, Drawer, Typography } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import type { MenuProps } from 'antd';
import { SidebarProps } from './types';
import { getMenuItemsByRole } from './menuConfig';
import { useAuthStore } from '../../store/authStore';

const { Sider } = Layout;
const { Title } = Typography;

/**
 * 사이드바 컴포넌트
 */
export const AppSidebar: React.FC<SidebarProps> = ({
  collapsed,
  onCollapse,
  isMobile,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();

  /**
   * 현재 사용자 역할에 따른 메뉴 아이템 필터링
   * - 부모 메뉴 역할 필터링
   * - 자식 메뉴(서브메뉴)도 역할별 필터링 적용
   */
  const menuItems: MenuProps['items'] = useMemo(() => {
    if (!user) return [];

    const filteredItems = getMenuItemsByRole(user.role);

    return filteredItems.map((item) => {
      // 서브메뉴가 있는 경우, 사용자 역할에 맞는 서브메뉴만 필터링
      const filteredChildren = item.children
        ?.filter((child) => child.roles.includes(user.role))
        .map((child) => ({
          key: child.key,
          icon: child.icon,
          label: child.label,
        }));

      return {
        key: item.key,
        icon: item.icon,
        label: item.label,
        children: filteredChildren && filteredChildren.length > 0
          ? filteredChildren
          : undefined,
      };
    });
  }, [user]);

  /**
   * 현재 경로에 해당하는 메뉴 키 선택
   */
  const selectedKeys = useMemo(() => {
    return [location.pathname];
  }, [location.pathname]);

  /**
   * 메뉴 클릭 핸들러
   */
  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    navigate(key);
    // 모바일에서는 메뉴 클릭 시 사이드바 닫기
    if (isMobile) {
      onCollapse(true);
    }
  };

  /**
   * 로고 클릭 시 랜딩페이지로 이동
   */
  const handleLogoClick = () => {
    navigate('/');
  };

  /**
   * 로고 영역 렌더링
   * - 클릭 시 랜딩페이지로 이동
   */
  const renderLogo = () => (
    <div
      onClick={handleLogoClick}
      style={{
        height: '64px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(255, 255, 255, 0.1)',
        cursor: 'pointer',
        transition: 'background 0.2s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
      }}
    >
      <Title
        level={4}
        style={{
          color: '#fff',
          margin: 0,
          fontSize: collapsed && !isMobile ? '16px' : '20px',
        }}
      >
        {collapsed && !isMobile ? '채점' : '📚 채점톡'}
      </Title>
    </div>
  );

  /**
   * 사이드바 콘텐츠 (로고 + 메뉴)
   */
  const sidebarContent = (
    <>
      {renderLogo()}
      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={selectedKeys}
        items={menuItems}
        onClick={handleMenuClick}
        style={{ borderRight: 0 }}
      />
    </>
  );

  /**
   * 모바일: Drawer 모드
   */
  if (isMobile) {
    return (
      <Drawer
        placement="left"
        onClose={() => onCollapse(true)}
        open={!collapsed}
        closable={false}
        width={256}
        styles={{
          body: { padding: 0, background: '#001529' },
        }}
      >
        {sidebarContent}
      </Drawer>
    );
  }

  /**
   * 데스크톱/태블릿: Sider 모드
   */
  return (
    <Sider
      collapsible
      collapsed={collapsed}
      onCollapse={onCollapse}
      breakpoint="lg"
      width={256}
      style={{
        overflow: 'auto',
        height: '100vh',
        position: 'fixed',
        left: 0,
        top: 0,
        bottom: 0,
      }}
    >
      {sidebarContent}
    </Sider>
  );
};
